import {
  type ScheduledMealOptionsDto,
  toBeverage,
  toScheduledMeal,
  toSupply,
} from '../../../../../../dto/order-options/site-order-options-dto';
import { Beverage, Route, ScheduledMeal, type Site, Supply } from '../../../../../../entities';
import SiteMenuBeverageOrder from '../../../../../../entities/site-menu-beverage-order';
import SiteMenuSupplyOrder from '../../../../../../entities/site-menu-supply-order';
import { type ServiceType } from '../../../../../../types';
import {
  flattenSiteMenuBeverageOrder,
  flattenSiteMenuSupplyOrder,
} from '../../../../../../utils/flatten-objects';
import { type EditableSiteMenuOrder } from '../../customer-order-form.utils';

export interface WizardItemValue {
  id?: string;
  quantity: number;
  beverage?: { id?: string };
  supply?: { id?: string };
}

/** The values of the wizard's form */
export interface WizardValues {
  routeId: string | null;
  scheduledMeal: { id?: string };
  serviceType: ServiceType;
  quantity: number;
  mainType: 'Regular' | 'Diabetic';
  showDiets: boolean;
  specialDiets?: string[] | null;
  showPreferences: boolean;
  preferences?: string[] | null;
  beverages: WizardItemValue[];
  extraBeverages: WizardItemValue[];
  supplies: WizardItemValue[];
  prepackBeverages: boolean;
  comment?: string;
}

/** The values the wizard starts with: those of "siteMenuOrder" when editing it, else the defaults */
export const toWizardValues = (siteMenuOrder?: EditableSiteMenuOrder): WizardValues => {
  if (!siteMenuOrder) {
    return {
      routeId: null,
      scheduledMeal: { id: undefined },
      serviceType: 'C1',
      quantity: 1,
      mainType: 'Regular',
      showDiets: false,
      showPreferences: false,
      beverages: [],
      extraBeverages: [],
      supplies: [],
      prepackBeverages: false,
    };
  }
  const beverageOrders = siteMenuOrder.siteMenuBeverageOrders ?? [];
  return {
    routeId: siteMenuOrder.route?.id ?? null,
    scheduledMeal: { id: siteMenuOrder.scheduledMeal?.id },
    serviceType: siteMenuOrder.serviceType ?? 'C1',
    quantity: siteMenuOrder.quantity ?? 1,
    mainType: siteMenuOrder.mainType ?? 'Regular',
    showDiets: (siteMenuOrder.diets?.length ?? 0) > 0,
    specialDiets: siteMenuOrder.diets,
    showPreferences: (siteMenuOrder.preferences?.length ?? 0) > 0,
    preferences: siteMenuOrder.preferences,
    beverages: beverageOrders.filter((b) => !b.isExtraBeverage).map(flattenSiteMenuBeverageOrder),
    extraBeverages: beverageOrders
      .filter((b) => b.isExtraBeverage)
      .map(flattenSiteMenuBeverageOrder),
    supplies: (siteMenuOrder.siteMenuSupplyOrders ?? []).map(flattenSiteMenuSupplyOrder),
    prepackBeverages: siteMenuOrder.prepackBeverages ?? false,
    comment: siteMenuOrder.comment,
  };
};

interface SiteMenuOrderContext {
  id: string | undefined;
  temporaryId: string;
  site: Site;
  scheduledMeal: ScheduledMealOptionsDto | undefined;
}

/** The site menu order made of the wizard's "values" */
export const toEditableSiteMenuOrder = (
  values: WizardValues,
  { id, temporaryId, site, scheduledMeal }: SiteMenuOrderContext
): EditableSiteMenuOrder => {
  const findRoute = (): Route | undefined =>
    values.routeId
      ? site.routes.find((route) => route.id === values.routeId) ?? new Route(values.routeId)
      : undefined;
  const allBeverages = [
    ...(scheduledMeal?.beverages ?? []),
    ...(scheduledMeal?.extraBeverages ?? []),
  ];

  const siteMenuSupplyOrders = values.supplies.map((item) => {
    const supplyOrder = new SiteMenuSupplyOrder();
    supplyOrder.id = item.id;
    const option = scheduledMeal?.supplies.find((supply) => String(supply.id) === item.supply?.id);
    supplyOrder.supply = option ? toSupply(option) : new Supply();
    supplyOrder.quantity = item.quantity;
    supplyOrder.site = site;
    const route = findRoute();
    if (route) supplyOrder.route = route;
    supplyOrder.unitPrice = supplyOrder.supply.price;
    supplyOrder.totalPrice = item.quantity * supplyOrder.supply.price;
    return supplyOrder;
  });

  const toBeverageOrders = (
    items: WizardItemValue[],
    isExtraBeverage: boolean
  ): SiteMenuBeverageOrder[] =>
    items.map((item) => {
      const beverageOrder = new SiteMenuBeverageOrder();
      beverageOrder.id = item.id;
      const option = allBeverages.find((beverage) => String(beverage.id) === item.beverage?.id);
      beverageOrder.beverage = option ? toBeverage(option) : new Beverage();
      beverageOrder.quantity = item.quantity;
      beverageOrder.site = site;
      const route = findRoute();
      if (route) beverageOrder.route = route;
      beverageOrder.unitPrice = beverageOrder.beverage.price;
      beverageOrder.totalPrice = item.quantity * beverageOrder.beverage.price;
      beverageOrder.isExtraBeverage = isExtraBeverage;
      return beverageOrder;
    });

  const route = site.routes.find((siteRoute) => siteRoute.id === values.routeId);
  const unitPrice = scheduledMeal?.price ?? 0;
  return {
    id,
    temporaryId,
    site,
    ...(route?.id ? { route } : {}),
    mealType: '',
    mainType: values.mainType,
    preferences: values.preferences ?? null,
    scheduledMeal: scheduledMeal ? toScheduledMeal(scheduledMeal) : new ScheduledMeal(),
    mealName: scheduledMeal?.name ?? 'UNKNOWN',
    unitPrice,
    totalPrice: values.quantity * unitPrice,
    quantity: values.quantity,
    diets: values.specialDiets ?? null,
    prepackBeverages: values.prepackBeverages,
    serviceType: values.serviceType,
    comment: values.comment,
    siteMenuSupplyOrders,
    siteMenuBeverageOrders: [
      ...toBeverageOrders(values.beverages, false),
      ...toBeverageOrders(values.extraBeverages, true),
    ],
  } as unknown as EditableSiteMenuOrder;
};
